import BiArrowRightShort from 'assets/icons/arrow-right-short.svg?component'
import DocIcon from 'assets/icons/document-icon.svg?component'
import DownloadIcon from 'assets/icons/download-icon.svg?component'
import { motion } from 'framer-motion'
import { MouseEventHandler, ReactNode } from 'react'
import tw, { TwStyle } from 'twin.macro'

import Loader from '../Loader'

// import { Caption } from '../Caption'
// import { BodyText } from '../Typography/BodyText'

export type ButtonType = 'primary' | 'ghost' | 'secondary' | 'text' | 'link' | 'download' | 'shadow'

type ButtonProps = {
  variant?: ButtonType
  children?: ReactNode
  caption?: string
  icon?: ReactNode
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  onClick?: MouseEventHandler<HTMLButtonElement>
  loading?: boolean
  twStyle?: TwStyle
}

const buttonVariants: { [key: string]: TwStyle } = {
  primary: tw`max-w-button bg-primary-middle text-white rounded-s30 shadow-button hover:bg-primary-dark transition-colors disabled:bg-primary-middle`,
  link: tw`text-primary p-0 active:text-primary-hover hover:text-primary-hover disabled:text-primary max-w-none w-auto min-w-0`,
  text: tw`text-primary-middle rounded-s20 py-4 active:text-primary-hover hover:text-primary-hover disabled:text-primary`,
  ghost: tw`border border-primary-middle bg-toggle-button text-primary rounded-s20 py-4 hover:border-primary active:text-primary-middle disabled:border-button disabled:text-primary`,
  secondary: tw`bg-primary-middle text-white rounded-s20 py-4 shadow-button hover:bg-primary-dark transition-colors disabled:bg-primary-middle`,
  download: tw`bg-secondary py-3 w-full min-w-0 rounded-2xl hover:border-button leading-s18`,
  shadow: tw`border border-grey-middle text-primary rounded-s20 py-4 hover:border-primary active:text-primary-middle disabled:border-button disabled:text-primary`
}

const styles = {
  button: ({ variant = 'text', twStyle, loading }: { variant: string; twStyle?: TwStyle; loading: boolean }) => [
    tw`w-full py-5 px-6 disabled:opacity-60 disabled:cursor-not-allowed`,
    buttonVariants[variant],
    twStyle,
    variant === 'ghost' && loading && tw`h-[51px] py-0`
  ]
}

const Button = ({
  variant = 'primary',
  children,
  caption = '',
  icon,
  disabled,
  type,
  onClick,
  loading = false,
  twStyle
}: ButtonProps) => {
  return (
    <motion.button
      css={styles.button({ variant, twStyle, loading })}
      disabled={loading || disabled}
      whileTap={{ scale: !disabled || !loading ? 0.97 : 1 }}
      type={type}
      onClick={onClick}
    >
      {
        {
          primary: <PrimaryButton loading={loading} text={children} caption={caption} icon={icon} />,
          ghost: <SecondaryButton loading={loading} text={children} icon={icon} />,
          secondary: <SecondaryButton loading={loading} text={children} icon={icon} />,
          text: <SecondaryButton loading={loading} text={children} icon={icon} />,
          link: <SecondaryButton loading={loading} text={children} icon={icon} variant={variant} />,
          download: <DownloadButton loading={loading} text={children} icon={icon} />,
          shadow: <SecondaryButton loading={loading} text={children} icon={icon} />
        }[variant]
      }
    </motion.button>
  )
}

const PrimaryButton = ({
  text,
  caption,
  icon = <BiArrowRightShort tw='text-2xl' />,
  loading
}: {
  text?: ReactNode
  caption?: string
  icon?: ReactNode
  loading?: boolean
}) => {
  return (
    <div tw='flex justify-between items-center space-x-2 w-full'>
      <div tw='flex flex-col items-start w-4/5'>
        {text}
        {/* <BodyText text={text} variant='bold' twStyle={tw`line-clamp-1`} /> */}
        {/* <Caption text={caption} twStyle={tw`text-white text-opacity-87 line-clamp-2`} /> */}
      </div>
      <div tw='flex flex-shrink-0 justify-center items-center w-1/5 max-w-[40px] h-[40px] bg-white text-black rounded-s14'>
        {loading ? <Loader variant='small' /> : icon}
      </div>
    </div>
  )
}

const SecondaryButton = ({
  icon,
  text,
  variant,
  loading
}: {
  icon?: ReactNode
  text?: ReactNode
  variant?: ButtonType
  loading?: boolean
}) => {
  return (
    <div css={[tw`flex justify-center items-center space-x-2 flex-none`, variant === 'link' && tw`justify-start`]}>
      {/* <BodyText text={text} variant='bold' twStyle={tw`line-clamp-1 text-s14`} /> */}
      {text}
      {loading ? <Loader variant='small' /> : icon}
    </div>
  )
}

const DownloadButton = ({
  text,
  before = <DocIcon />,
  icon = <DownloadIcon />,
  loading
}: {
  text: ReactNode
  before?: ReactNode
  icon?: ReactNode
  loading?: boolean
}) => {
  return (
    <div tw='flex justify-between items-center'>
      <div tw='flex flex-shrink-0 justify-center items-center w-1/5 max-w-[40px] h-[40px] text-black rounded-s14'>
        {before}
      </div>
      <div tw='flex flex-col items-start w-4/5'>
        {/* <BodyText text={text} variant='bold' twStyle={tw`text-s14`} /> */}
      </div>
      <div tw='flex justify-center items-center w-[20px] h-[20px] bg-primary rounded-full '>
        {loading ? <Loader variant='small' /> : icon}
      </div>
    </div>
  )
}

export { Button }
